import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from 'context/ToastContext';
import * as apiSettingsDirs from 'api/apiSettingsDirs';
import type { SourceLeadType } from '../../types';
import { SETTINGS_DIR_NAME_MAX_LENGTH } from '../../types';
import type { SourceLeadEditState } from '../uiTypes';

const useSourceLeads = () => {
    const { t } = useTranslation(['Settings', 'common']);
    const { showToast } = useToast();
    const [rows, setRows] = useState<SourceLeadType[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [newRow, setNewRow] = useState<SourceLeadEditState>({ sourceName: '', sourceAllowDomain: '' });
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingRow, setEditingRow] = useState<SourceLeadEditState>({ sourceName: '', sourceAllowDomain: '' });

    const fetchRows = async () => {
        setIsLoading(true);
        const result = await apiSettingsDirs.getSourceLeads();
        setIsLoading(false);
        if (!result) return showToast(t('common:messError'), 'danger');
        setRows(result);
    };

    useEffect(() => {
        fetchRows();
    }, []);

    const validate = (value: SourceLeadEditState): string | null => {
        if (!String(value.sourceName || '').trim()) return t('sourceLeads.errors.nameRequired');
        if (String(value.sourceName || '').trim().length > SETTINGS_DIR_NAME_MAX_LENGTH) {
            return t('sourceLeads.errors.nameTooLong', { max: SETTINGS_DIR_NAME_MAX_LENGTH });
        }
        return null;
    };

    const normalizeAllowedDomains = (value: string): string =>
        String(value || '')
            .split(',')
            .map((domain) => domain.trim())
            .filter(Boolean)
            .join(',');

    const createSourceLead = async () => {
        const err = validate(newRow);
        if (err) return showToast(err, 'danger');
        setIsCreating(true);
        const result = await apiSettingsDirs.createSourceLead({
            sourceName: newRow.sourceName.trim(),
            sourceAllowDomain: normalizeAllowedDomains(newRow.sourceAllowDomain),
        });
        setIsCreating(false);
        if (!result?.stat) return showToast(t('sourceLeads.errors.createFailed'), 'danger');
        setNewRow({ sourceName: '', sourceAllowDomain: '' });
        showToast(t('sourceLeads.toasts.created'), 'success');
        await fetchRows();
    };

    const beginEdit = (row: SourceLeadType) => {
        setEditingId(row.ID);
        setEditingRow({
            sourceName: row.sourceName || '',
            sourceAllowDomain: row.sourceAllowDomain || '',
        });
    };

    const saveEdit = async (row: SourceLeadType) => {
        const err = validate(editingRow);
        if (err) return showToast(err, 'danger');
        const result = await apiSettingsDirs.updateSourceLead(row.ID, {
            sourceName: editingRow.sourceName.trim(),
            sourceAllowDomain: normalizeAllowedDomains(editingRow.sourceAllowDomain),
        });
        if (!result?.stat) return showToast(t('sourceLeads.errors.saveFailed'), 'danger');
        setEditingId(null);
        showToast(t('sourceLeads.toasts.updated'), 'success');
        await fetchRows();
    };

    const deleteRow = async (row: SourceLeadType) => {
        if (!window.confirm(t('sourceLeads.confirmDelete', { name: row.sourceName }))) return;
        const result = await apiSettingsDirs.deleteSourceLead(row.ID);
        if (!result?.stat) return showToast(t('sourceLeads.errors.deleteFailed'), 'danger');
        showToast(t('sourceLeads.toasts.deleted'), 'success');
        await fetchRows();
    };

    return {
        rows,
        isLoading,
        isCreating,
        newRow,
        setNewRow,
        editingId,
        setEditingId,
        editingRow,
        setEditingRow,
        createSourceLead,
        beginEdit,
        saveEdit,
        deleteRow,
    };
};

export default useSourceLeads;
